import { Select } from '@automata/ui';
import { logger } from '@automata/utils';
import { useGetSchedulerVersions } from '@automata/wfb-api';
import { MenuItem, type SelectChangeEvent } from '@mui/material';
import { type FC, useMemo } from 'react';

import { selectSchedulerVersion } from '../../features/workflow/selectors';
import { setSchedulerVersion } from '../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { sortVersionsDescending } from '../../utils/sortVersions';

export type SchedulerVersionSelectorProps = {
  disabled?: boolean;
};

export const SchedulerVersionSelector: FC<SchedulerVersionSelectorProps> = ({
  disabled = false,
}) => {
  const dispatch = useAppDispatch();
  const selectedVersion = useAppSelector(selectSchedulerVersion);

  const { data: schedulerVersionsData, isLoading } = useGetSchedulerVersions(
    {},
    {
      onError: (error) => {
        logger.error('Error fetching scheduler versions:', error);
      },
    }
  );

  const availableVersions = useMemo(() => {
    if (!schedulerVersionsData) return [];

    const maestroVersions = schedulerVersionsData['maestro'] || [];

    return sortVersionsDescending(maestroVersions);
  }, [schedulerVersionsData]);

  const handleVersionChange = (event: SelectChangeEvent<unknown>) => {
    if (typeof event.target.value !== 'string' || event.target.value === '') {
      return;
    }

    dispatch(setSchedulerVersion(event.target.value));
  };

  const currentValue =
    selectedVersion && availableVersions.includes(selectedVersion)
      ? selectedVersion
      : availableVersions[0] || '';

  return (
    <Select
      label="Scheduler engine version"
      value={currentValue}
      onChange={handleVersionChange}
      disabled={disabled || isLoading}
      placeholder="Select scheduler engine version"
    >
      {availableVersions.map((version) => (
        <MenuItem key={version} value={version}>
          {version}
        </MenuItem>
      ))}
    </Select>
  );
};
